/**
 * Comprehensive error handling utility for the administration portal
 * Handles all API error formats and provides user-friendly German messages
 */

export interface ApiErrorResponse {
  message?: string;
  error?: string | { message?: string };
  statusCode?: number;
  code?: string;
}

// Map of common error codes to user-friendly German messages
const ERROR_MESSAGES: Record<string, string> = {
  'INVALID_CREDENTIALS': 'Die E-Mail-Adresse oder das Passwort ist falsch.',
  'USER_NOT_FOUND': 'Kein Benutzer mit dieser E-Mail-Adresse gefunden.',
  'INVALID_PASSWORD': 'Das eingegebene Passwort ist falsch.',
  'ACCOUNT_LOCKED': 'Ihr Konto wurde gesperrt. Wenden Sie sich an den Administrator.',
  'ACCOUNT_DISABLED': 'Ihr Konto wurde deaktiviert.',
  'TOKEN_EXPIRED': 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
  'UNAUTHORIZED': 'Sie haben keine Berechtigung für diese Aktion.',
  'FORBIDDEN': 'Zugriff verweigert.',
  'NOT_FOUND': 'Die angeforderte Ressource wurde nicht gefunden.',
  'VALIDATION_ERROR': 'Die eingegebenen Daten sind ungültig.',
  'DUPLICATE_EMAIL': 'Diese E-Mail-Adresse wird bereits verwendet.',
  'NETWORK_ERROR': 'Verbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung.',
  'SERVER_ERROR': 'Ein Serverfehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
  'TOO_MANY_REQUESTS': 'Zu viele Anfragen. Bitte warten Sie einen Moment.',
};

// HTTP status code to German message mapping
const STATUS_CODE_MESSAGES: Record<number, string> = {
  400: 'Ungültige Anfrage. Bitte überprüfen Sie Ihre Eingaben.',
  401: 'Anmeldung erforderlich. Bitte melden Sie sich an.',
  403: 'Sie haben keine Berechtigung für diese Aktion.',
  404: 'Die angeforderte Ressource wurde nicht gefunden.',
  409: 'Konflikt: Die Ressource existiert bereits oder kann nicht geändert werden.',
  422: 'Die übermittelten Daten konnten nicht verarbeitet werden.',
  429: 'Zu viele Anfragen. Bitte warten Sie einen Moment.',
  500: 'Interner Serverfehler. Bitte versuchen Sie es später erneut.',
  502: 'Server nicht erreichbar. Bitte versuchen Sie es später erneut.',
  503: 'Service nicht verfügbar. Bitte versuchen Sie es später erneut.',
  504: 'Zeitüberschreitung. Bitte versuchen Sie es erneut.',
};

/**
 * Extract a human-readable error message from any error format
 * @param error - Any type of error (string, Error, API error object, etc.)
 * @param fallbackMessage - Default message if no specific message can be extracted
 * @param context - Context for the error (e.g., 'login', 'customer-load')
 * @returns A user-friendly German error message
 */
export function extractErrorMessage(
  error: any,
  fallbackMessage?: string,
  context?: string
): string {
  // If it's already a string, return it
  if (typeof error === 'string') {
    return error || fallbackMessage || 'Ein unbekannter Fehler ist aufgetreten.';
  }

  // Handle null/undefined
  if (!error) {
    return fallbackMessage || 'Ein unbekannter Fehler ist aufgetreten.';
  }

  // Handle API error response objects
  if (typeof error === 'object') {
    // Check for API error code first
    if (error.code && ERROR_MESSAGES[error.code]) {
      return ERROR_MESSAGES[error.code];
    }

    // Extract message from various object structures
    let message = '';

    // Try error.message first (specific messages take priority over generic status code messages)
    if (error.message) {
      if (typeof error.message === 'string') {
        message = error.message;
      } else if (typeof error.message === 'object' && error.message.message) {
        if (Array.isArray(error.message.message)) {
          message = error.message.message.join('\n');
        } else {
          message = error.message.message;
        }
      }
    }

    // Try error.error if no message found
    if (!message && error.error) {
      if (typeof error.error === 'string') {
        message = error.error;
      } else if (typeof error.error === 'object' && error.error.message) {
        message = error.error.message;
      }
    }

    // Try other common error properties
    if (!message) {
      message = error.description || error.detail || error.msg || '';
    }

    // If we found a message, clean it up and return
    if (message) {
      return cleanErrorMessage(message);
    }

    // Fall back to generic HTTP status code message
    if (error.statusCode && STATUS_CODE_MESSAGES[error.statusCode]) {
      return STATUS_CODE_MESSAGES[error.statusCode];
    }
  }

  // Handle standard JavaScript Error objects
  if (error instanceof Error) {
    return cleanErrorMessage(error.message);
  }

  // Last resort: check if error has toString method
  if (typeof error.toString === 'function') {
    const stringified = error.toString();
    if (stringified !== '[object Object]') {
      return cleanErrorMessage(stringified);
    }
  }

  // Context-specific fallback messages
  if (context) {
    switch (context) {
      case 'login':
        return 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten.';
      case 'load-customers':
        return 'Fehler beim Laden der Kundendaten.';
      case 'load-system-status':
        return 'Fehler beim Laden des Systemstatus.';
      case 'save':
        return 'Fehler beim Speichern.';
      case 'delete':
        return 'Fehler beim Löschen.';
      case 'network':
        return 'Verbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung.';
      default:
        break;
    }
  }

  return fallbackMessage || 'Ein unbekannter Fehler ist aufgetreten.';
}

/**
 * Clean up error messages for better user experience
 */
function cleanErrorMessage(message: string): string {
  if (!message || typeof message !== 'string') {
    return 'Ein unbekannter Fehler ist aufgetreten.';
  }

  // Remove technical prefixes
  let cleaned = message
    .replace(/^Error:\s*/i, '')
    .replace(/^HTTP Error:\s*/i, '')
    .replace(/^API Error:\s*/i, '')
    .replace(/^Request failed:\s*/i, '')
    .trim();

  // Replace common English phrases with German equivalents
  const replacements: [string | RegExp, string][] = [
    [/network error/gi, 'Verbindungsfehler'],
    [/connection failed/gi, 'Verbindung fehlgeschlagen'],
    [/timeout/gi, 'Zeitüberschreitung'],
    [/invalid credentials/gi, 'Ungültige Anmeldedaten'],
    [/unauthorized/gi, 'Nicht autorisiert'],
    [/forbidden/gi, 'Zugriff verweigert'],
    [/not found/gi, 'Nicht gefunden'],
    [/internal server error/gi, 'Interner Serverfehler'],
    [/bad request/gi, 'Ungültige Anfrage'],
  ];

  for (const [pattern, replacement] of replacements) {
    cleaned = cleaned.replace(pattern, replacement);
  }

  return cleaned || 'Ein unbekannter Fehler ist aufgetreten.';
}

/**
 * Create an error handler for toast notifications
 */
export function createToastErrorHandler(context?: string) {
  return (error: any, fallbackMessage?: string) => {
    return extractErrorMessage(error, fallbackMessage, context);
  };
}

/**
 * Check if an error is likely a network error
 */
export function isNetworkError(error: any): boolean {
  if (typeof error === 'string') {
    return /network|connection|timeout|offline/i.test(error);
  }

  if (error instanceof Error) {
    return /network|connection|timeout|offline/i.test(error.message) ||
           error.name === 'NetworkError' ||
           error.name === 'TypeError';
  }

  if (error && typeof error === 'object') {
    return error.code === 'NETWORK_ERROR' ||
           error.name === 'NetworkError' ||
           (error.statusCode && error.statusCode === 0);
  }

  return false;
}

/**
 * Check if an error is likely an authentication error
 */
export function isAuthError(error: any): boolean {
  if (error && typeof error === 'object') {
    return error.statusCode === 401 ||
           error.code === 'UNAUTHORIZED' ||
           error.code === 'TOKEN_EXPIRED' ||
           error.code === 'INVALID_CREDENTIALS';
  }

  return false;
}